import { isLeft } from "fp-ts/lib/Either";
import { inject } from "mobx-react";
import { useMemo } from "react";

import { ShareButtons } from "../../../../core/components/integrations/ShareButtons.component";
import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import { HtmlEmbed } from "../../../../core/content-blocks/HtmlEmbed.component";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { generateImageAssetSizes } from "@inferno/renderer-shared-core";
import { placeholderImage } from "@ihr-radioedit/inferno-core";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Container, Heading } from "../../../../core/ui";
import { getCoastLinks } from "../../lib";
import { CoastContentBlock } from "./CoastContent.component";
import { CoastGuestPayloadResolver } from "./CoastContent.types";
import { CoastGuestPastShowItem } from "./CoastGuestPastShowItem.component";
import { RESPONSIVE_IMAGE } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("CoastContentGuest");

export type CoastContentGuestProps = {
  content: Webapi.PublishRecordFragment;
  eyebrow?: Webapi.Eyebrow;
  store?: Store;
};

export const CoastContentGuest = inject("store")(({ content, store }: CoastContentGuestProps) => {
  if (!store) {
    return null;
  }

  const { site, request } = store;
  const { env } = store;

  const resolvedPayload = CoastGuestPayloadResolver.decode(content.payload);

  if (isLeft(resolvedPayload)) {
    log.error(`Payload failed validation: ${getPaths(resolvedPayload)}`);
    return null;
  }

  const payload = resolvedPayload.right;
  const { summary, fields } = payload;

  const { srcset, sizes } = generateImageAssetSizes(formatImage(summary.image.url, env.IMAGE_HOST));
  const placeholderSrc = `${placeholderImage}?ops=resize(900,506),quality(10)`;

  const shareUrl = site.getShareUrl(request);

  const pastShows = useMemo(() => getCoastLinks(content, "shows"), [content]);

  return (
    <Container className="component-coast-content-guest coast-content-layout-container">
      <Heading level={1}>{content.summary.title}</Heading>

      <ShareButtons url={shareUrl} title={content.summary.title} desc={content.summary.description} setStyles={false} />

      <article>
        <div className="item-thumb">
          <ResponsiveImage
            alt={summary.image.alt_text || summary.title || ""}
            src={placeholderSrc}
            placeholder={placeholderSrc}
            srcset={srcset}
            sizes={sizes}
            initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
            initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
          />
        </div>

        <section>
          <Heading level={3}>Biography:</Heading>
          {payload.fields.blocks.value?.map((block, i) => (
            <CoastContentBlock block={block} key={i} />
          ))}
        </section>

        {fields.websites?.value ? (
          <section>
            <Heading level={3}>Website(s):</Heading>
            <HtmlEmbed {...fields.websites} />
          </section>
        ) : null}

        {fields.books?.value ? (
          <section>
            <Heading level={3}>Book(s):</Heading>
            <HtmlEmbed {...fields.books} />
          </section>
        ) : null}

        {fields.cds?.value ? (
          <section>
            <Heading level={3}>CDs:</Heading>
            <HtmlEmbed {...fields.cds} />
          </section>
        ) : null}

        {fields.videos?.value ? (
          <section>
            <Heading level={3}>Video(s):</Heading>
            <HtmlEmbed {...fields.videos} />
          </section>
        ) : null}

        {pastShows?.length ? (
          <section className="past-shows">
            <Heading level={3}>Past Shows:</Heading>

            <ul>
              {pastShows.map(show => (
                <CoastGuestPastShowItem show={show} key={show.ref_id} />
              ))}
            </ul>
          </section>
        ) : null}
      </article>

      <ShareButtons url={shareUrl} title={content.summary.title} desc={content.summary.description} setStyles={false} />

      {/* TODO: Previous-next */}
    </Container>
  );
});

export default CoastContentGuest;
